import { pricingPlansModalOpenAtom } from '@/features/settings/billing/billingAtoms';
import { useIsPracticeMaxUpgradeAvailable } from '@/features/settings/billing/hooks/useIsPracticeMaxUpgradeAvailable';
import { useSubscriptionUpgrade } from '@/features/settings/billing/hooks/useSubscriptionUpgrade';
import { useUpdateExistingSubscription } from '@/features/settings/billing/hooks/useUpdateExistingSubscription';
import { isCancelPendingSubscription as getIsCancelPendingSubscription } from '@/features/settings/billing/utils/isCancelPendingSubscription';
import {
  getNewProductTierProduct,
  getPlanFromPeriod,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isClinicianMaxBillingSeat } from '@heidi/commerce';
import { type Period } from '@heidi/commerce/contracts';
import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

export function usePricingPlansModalState() {
  const setIsOpen = useSetAtom(pricingPlansModalOpenAtom);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const { data: billingInfo } = useQueryUserBillingInfo();
  const legacyTier = billingInfo?.tier;
  const currentPlan = billingInfo?.new_product_tier;
  const currentSeatType = billingInfo?.seat_type;

  const { isInReverseTrial, isFakeClinicianTrial } = useReverseTrial();
  const { isInFreeTrial: isEvidenceInFreeTrial } = useEvidenceBillingInfo();

  const isTrialingClinician = isInReverseTrial || isFakeClinicianTrial;
  /**
   * Standard = `CLINICIAN_PRO` and not MAX. Absent `seat_type` still reads as Standard.
   */
  const isClinicianMaxSeat = isClinicianMaxBillingSeat(billingInfo);
  const isClinicianStandardPlan =
    currentPlan === SubscriptionTier.CLINICIAN_PRO && !isClinicianMaxSeat;
  const isPracticeMaxSeat =
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS &&
    currentSeatType === BillingSeatType.MAX;
  const isPracticeStandardPlan =
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS && !isPracticeMaxSeat;
  /** Flag on and the (CLINICIAN_PRO_PLUS, MAX) row listed: the Practice column offers a Max seat. */
  const { isPracticeMaxUpgradeAvailable: offersPracticeMax } =
    useIsPracticeMaxUpgradeAvailable();
  const isTrialing = isInReverseTrial || isFakeClinicianTrial || isEvidenceInFreeTrial;

  const isCancelPendingSubscription = getIsCancelPendingSubscription(
    billingInfo,
    isTrialing
  );

  const { redirectToBillingPortalReactivate } = useUpdateExistingSubscription();

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const { data: products, catalogueVersion } = useQueryProducts({
    heidi2Catalogue: true,
  });
  const handlePlanCheckout = useCallback(
    (period: Period, tier: SubscriptionTier, seatType = BillingSeatType.STANDARD) => {
      if (isAppleCompliantBuild()) return;
      // v3 rows checkout by `price_id` (tier + seat). A Max seat exists on v3
      // only, so `null` blocks it rather than falling back to tier checkout.
      const priceId =
        catalogueVersion === 'v3'
          ? (getPlanFromPeriod(period, getNewProductTierProduct(tier, products, seatType))
              ?.id ?? null)
          : seatType === BillingSeatType.MAX
            ? null
            : undefined;
      void handleUpgrade(period, tier, priceId);
    },
    [catalogueVersion, handleUpgrade, products]
  );

  const handleReactivateSubscription = useCallback(() => {
    if (isAppleCompliantBuild()) return;
    void redirectToBillingPortalReactivate(handleClose);
  }, [handleClose, redirectToBillingPortalReactivate]);

  return {
    handlePlanCheckout,
    handleReactivateSubscription,
    handleClose,
    isPending,
    legacyTier,
    currentPlan,
    currentSeatType,
    isTrialingClinician,
    isClinicianStandardPlan,
    isClinicianMaxSeat,
    isPracticeStandardPlan,
    isPracticeMaxSeat,
    offersPracticeMax,
    isCancelPendingSubscription,
    isInReverseTrial,
    isFakeClinicianTrial,
  };
}

export type UsePricingPlansModalStateReturn = ReturnType<
  typeof usePricingPlansModalState
>;
